import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi, SHARED_ORIGIN } from "@/lib/api";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { formatDealPrice } from "@/lib/dealPrice";
import { FileDown, ExternalLink, Ticket, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700",
  redeemed: "bg-slate-100 text-slate-500",
  expired: "bg-red-50 text-red-600",
  // Iter 754 — vouchers of a vendor-deleted deal are cancelled; show greyed.
  cancelled: "bg-slate-100 text-slate-400",
};

const API_ORIGIN = SHARED_ORIGIN;

export default function MyVouchersPage() {
  const q = useQuery({
    queryKey: ["my-vouchers"],
    queryFn: async () => (await sharedApi.get("/classifieds/deal-vouchers/my")).data,
    retry: 0,
  });

  const vouchers = q.data?.vouchers || [];

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
      <Seo title="My vouchers" />
      <Breadcrumbs items={[{ label: "My vouchers" }]} />

      <div className="mt-6 mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">My vouchers</h1>
        <p className="mt-2 text-slate-500 text-sm">All the deals you&apos;ve claimed. Show the QR at the store to redeem.</p>
      </div>

      {q.isLoading && (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
      )}

      {!q.isLoading && vouchers.length === 0 && (
        <div className="rounded-3xl bg-white shadow-card p-10 text-center" data-testid="my-vouchers-empty">
          <Ticket className="w-10 h-10 mx-auto text-slate-300" />
          <h2 className="mt-4 font-display text-xl font-bold text-slate-900">No vouchers yet</h2>
          <p className="mt-1 text-sm text-slate-500">Grab your first deal to see it here.</p>
          <Link to="/deals" data-testid="my-vouchers-browse" className="mt-5 inline-block rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-5 py-2.5 transition-colors">
            Browse deals
          </Link>
        </div>
      )}

      {vouchers.length > 0 && (
        <div className="grid gap-4" data-testid="my-vouchers-list">
          {vouchers.map((v) => {
            const status = v.status || "active";
            const chip = STATUS_STYLES[status] || STATUS_STYLES.active;
            const pdfHref = v.pdf_url
              ? v.pdf_url.startsWith("http") ? v.pdf_url : `${API_ORIGIN}${v.pdf_url}`
              : null;
            return (
              <div
                key={v.id}
                className="rounded-2xl bg-white shadow-card p-5 border border-soft flex flex-col md:flex-row md:items-center gap-4"
                data-testid={`my-voucher-${v.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${chip}`} data-testid={`my-voucher-status-${v.id}`}>
                      {status}
                    </span>
                    {v.expires_at && status === "active" && (
                      <span className="text-[11px] text-slate-500">
                        Expires {new Date(v.expires_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 font-display text-lg font-bold text-slate-900 truncate">{v.deal_title}</div>
                  <div className="mt-0.5 text-sm text-slate-600">{v.merchant}</div>
                  {(() => {
                    const fmt = formatDealPrice(v);
                    return fmt.payLine ? (
                      <div className="mt-1.5 text-sm font-semibold text-slate-800" data-testid={`my-voucher-pay-line-${v.id}`}>
                        {fmt.payLine}
                      </div>
                    ) : null;
                  })()}
                  <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-slate-900 tabular-nums font-mono text-sm">
                    <span className="text-[10px] uppercase tracking-widest text-slate-400">Code</span>
                    <span data-testid={`my-voucher-code-${v.id}`}>{v.code}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {pdfHref && (
                    <a
                      href={pdfHref}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`my-voucher-pdf-${v.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 transition-colors"
                    >
                      <FileDown className="w-4 h-4" /> PDF
                    </a>
                  )}
                  {v.redeem_url && (
                    <Link
                      to={`/redeem/${v.id}`}
                      data-testid={`my-voucher-open-${v.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 font-semibold text-sm bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" /> Show
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
